import { PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects'
import * as userApi from 'src/services/user/userApi'
import { actions as appActions } from '../state'
import { ReturnCall } from '../types'
import TaxCouponApi from './TaxCouponApi'
import { selectBarcodeAttempted, selectTaxCouponData } from './TaxCouponSelectors'
import { actions } from './TaxCouponState'
import {
  TaxCouponCreateBatchPayload,
  TaxCouponCreateReceivementPayload,
  TaxCouponValidationBarcodePayload,
  EntryDataWithFC,
  ReceivementUnitChangePayload,
  GetDirectGondola,
} from './TaxCouponTypes'

const barcodeNotFound = 'NAO CADASTRADO'

function* createTaxCouponReceivement(action: PayloadAction<TaxCouponCreateReceivementPayload>) {
  yield put(appActions.isLoading(true))
  try {
    const { data: { idRecebimento } }: ReturnCall<typeof TaxCouponApi.createTaxCouponReceivement> = yield call(TaxCouponApi.createTaxCouponReceivement, action.payload)
    yield put(actions.setReceivement(idRecebimento))
  } catch (error: any) {
    toast.error(error?.response?.data?.message ?? 'Ocorreu um erro ao criar recebimento')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* validateEntryData(action: PayloadAction<EntryDataWithFC>) {
  yield put(appActions.isLoading(true))
  const { userBadge, idFc } = action.payload

  try {
    const { data, isOk } = yield call(userApi.validUserByBarcode, userBadge)

    if (!isOk) {
      toast.error(data.message)
      return
    }

    yield put(actions.setEntryData(action.payload))
    yield call(createTaxCouponReceivement, { payload: { idFc, userBadge }, type: '' })
  } catch (error: any) {
    toast.error(`Ocorreu um erro: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* scanBarcode(action: PayloadAction<TaxCouponValidationBarcodePayload>) {
  yield put(appActions.isLoading(true))
  const { payload } = action
  const { barcode } = payload

  try {
    const attempted = yield select(selectBarcodeAttempted)

    if (!barcode) {
      toast.info('Nenhum código de barras informado. Tente novamente')
      return
    }

    const { descProduto: description, idModelo, scannedIdMeasure } = yield call(
      TaxCouponApi.getProductByBarcode, payload,
    )

    if (attempted === barcode && idModelo === barcodeNotFound) {
      toast.info('Necessário buscar produto por descrição')
      yield put(actions.shouldOpenSearchModal(true))
      return
    }

    if (idModelo === barcodeNotFound) {
      yield put(actions.setBarcodeAttempted(barcode))
      toast.info('Tente inserir o código de barras novamente')
      yield put(actions.setBarcodeData({ idModelo, description }))
      return
    }

    yield put(actions.setBarcodeAttempted(''))
    yield put(actions.updateIdMeasureUnit({ key: 'idMeasure', value: scannedIdMeasure }))
    yield put(actions.setBarcodeData({ idModelo, description }))
    yield put(actions.setReceivementUnitChange({ receivementUnitChange: false }))
  } catch (error: any) {
    toast.error(`Ocorreu um erro: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* sendTaxCouponEntry(action: PayloadAction<any>) {
  yield put(appActions.isLoading(true))
  const { payload: history } = action

  try {
    const taxCouponData = yield select(selectTaxCouponData)
    yield call(TaxCouponApi.sendTaxCouponEntry, taxCouponData)
    history.push('/tax-coupon/finish')
  } catch (error: any) {
    toast.error(error?.response?.data?.message ?? 'Ocorreu um erro ao finalizar o cupom fiscal')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* createTaxCouponBatch(action: PayloadAction<TaxCouponCreateBatchPayload>) {
  yield put(appActions.isLoading(true))
  try {
    const { onSuccess } = action.payload
    const { data }: ReturnCall<typeof TaxCouponApi.createTaxCouponBatch> = yield call(TaxCouponApi.createTaxCouponBatch, action.payload)
    yield put(actions.setBatch(data.batch.idLote))
    yield put(actions.setReceivementUnitChange({ receivementUnitChange: false }))
    onSuccess?.(data.batch, data.userName)
  } catch (error: any) {
    toast.error(error?.response?.data?.message ?? 'Ocorreu um erro ao criar o lote do cupom fiscal')
    yield put(actions.setReceivementUnitChange({ receivementUnitChange: false }))
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* deleteTaxCouponBatch(action: PayloadAction<number>) {
  yield put(appActions.isLoading(true))
  try {
    const result: ReturnCall<typeof TaxCouponApi.deleteTaxCouponBatch> = yield call(TaxCouponApi.deleteTaxCouponBatch, action.payload)
    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Ocorreu um erro ao apagar o lote do cupom fiscal')
      return
    }
    yield put(actions.deleteBatchFromItems(action.payload))
    toast.success('Lote apagado com sucesso')
  } catch (error: any) {
    toast.error(error?.response?.data?.message ?? 'Ocorreu um erro ao apagar o lote do cupom fiscal')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getDirectGondola(action: PayloadAction<GetDirectGondola>) {
  yield put(appActions.isLoading(true))
  try {
    const data: ReturnCall<typeof TaxCouponApi.getDirectGondola> = yield call(TaxCouponApi.getDirectGondola, action.payload.idReceivement)
    if (data.data.batchesDirectGondola.length === 0) {
      toast.info('Nenhum lote direto gôndola encontrado')
    }
    yield put(actions.setDirectGondola({ batchesDirectGondola: data.data.batchesDirectGondola, directGondolaPrintLimit: data.data.directGondolaPrintLimit }))

    if (data.data.batchesDirectGondola.length > 0) {
      action.payload.setShowModal(true)
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message ?? 'Ocorreu um erro ao buscar os lotes direto gôndola')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* receivementUnitChange(action: PayloadAction<ReceivementUnitChangePayload>) {
  yield put(appActions.isLoading(true))
  try {
    yield call(TaxCouponApi.receivementUnitChange, action.payload)
    yield put(actions.setReceivementUnitChange({ receivementUnitChange: true }))
    action.payload.setShowModal(false)
  } catch (error: any) {
    const errorCodeUnauthorized = 401
    const { setError } = action.payload

    if (error?.response?.status === errorCodeUnauthorized) setError('Crachá inválido')

    toast.error(error?.response?.data?.message ?? 'Ocorreu um erro ao verificar crachá')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.validateEntryData.type, validateEntryData),
    takeLatest(actions.scanBarcode.type, scanBarcode),
    takeLatest(actions.sendTaxCouponEntry.type, sendTaxCouponEntry),
    takeLatest(actions.createTaxCouponReceivement.type, createTaxCouponReceivement),
    takeLatest(actions.deleteTaxCouponBatch.type, deleteTaxCouponBatch),
    takeLatest(actions.createTaxCouponBatch.type, createTaxCouponBatch),
    takeLatest(actions.getDirectGondola.type, getDirectGondola),
    takeLatest(actions.getReceivementUnitChange, receivementUnitChange),
  ])
}
